import React, { useMemo, useRef, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FiSettings } from "react-icons/fi";
import { AiOutlineDashboard } from "react-icons/ai";
import { LuLayoutDashboard } from "react-icons/lu";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { FaFileArchive, FaFileInvoice, FaChevronUp } from "react-icons/fa";
import { GiStockpiles } from "react-icons/gi";
import { RiStockFill } from "react-icons/ri";
import { FaCodePullRequest } from "react-icons/fa6";

import { useTenant } from "../../hooks";

const MenuSide = () => {
  const [t, i18n] = useTranslation("global");

  const { tenantUsername } = useTenant();

  const tenantFromPath = window.location.pathname
    .split("/")
    .filter(Boolean)[0];

  const tenantForRoute = tenantUsername || tenantFromPath;

  const Path = window.location.pathname;

  const scrollContainerRef = useRef(null);

  const [hoveredItemId, setHoveredItemId] = useState(null);

  const [hoverTooltip, setHoverTooltip] = useState(null);

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const tooltipHideTimeoutRef = useRef(null);

  const activeBackgroundColor = "var(--allowed)";
  const hoverBackgroundColor = "var(--allowed)";

  const menuItems = useMemo(
    () => [
      {
        id: "dashboard",
        title: t("menu.Dashboard"),
        to: tenantForRoute ? `/${tenantForRoute}/dashboard` : "/notfound",
        icon: AiOutlineDashboard,
        size: 22,
        active: Path === `/${tenantForRoute}/dashboard`,
      },

      {
        id: "warehouse_management",
        title: t("menu.warehouse_management"),
        to: tenantForRoute
          ? `/${tenantForRoute}/warehouses`
          : "/notfound",
        icon: RiStockFill,
        size: 20,
        active: Path.includes("/warehouses"),
      },

      {
        id: "products",
        title: t("menu.Products"),
        to: tenantForRoute
          ? `/${tenantForRoute}/products-home`
          : "/notfound",
        icon: LuLayoutDashboard,
        size: 20,
        active: Path.includes("/products-home"),
      },

      {
        id: "sales_order",
        title: t("menu.sales_order"),
        to: tenantForRoute
          ? `/${tenantForRoute}/invoice-sales-requisition`
          : "/notfound",
        icon: FaFileArchive,
        size: 18,
        active: Path.includes("/invoice-sales-requisition"),
      },

      {
        id: "purchase_orders",
        title: t("menu.purchase_orders"),
        to: tenantForRoute
          ? `/${tenantForRoute}/invoice-requisition`
          : "/notfound",
        icon: FaFileInvoice,
        size: 18,
        active: Path.includes("/invoice-requisition"),
      },

      {
        id: "requisition_management",
        title: t("menu.requisition_management"),
        to: tenantForRoute
          ? `/${tenantForRoute}/requisition`
          : "/notfound",
        icon: FaCodePullRequest,
        size: 18,
        active: Path.includes("/requisition"),
      },

      {
        id: "inventory_management",
        title: t("menu.inventory_management"),
        to: tenantForRoute
          ? `/${tenantForRoute}/stocktakings`
          : "/notfound",
        icon: GiStockpiles,
        size: 20,
        active: Path.includes("/stocktakings"),
      },

      {
        id: "reports",
        title: t("menu.reports"),
        to: tenantForRoute
          ? `/${tenantForRoute}/reports`
          : "/notfound",
        icon: HiOutlineDocumentReport,
        size: 20,
        active: Path.includes("/reports"),
      },

      {
        id: "settings",
        title: t("menu.Settings"),
        to: tenantForRoute
          ? `/${tenantForRoute}/settings`
          : "/notfound",
        icon: FiSettings,
        size: 20,
        active: Path.includes("/settings"),
      },
    ],
    [t, tenantForRoute, Path],
  );

  useEffect(() => {
    return () => {
      if (tooltipHideTimeoutRef.current) {
        clearTimeout(tooltipHideTimeoutRef.current);
      }
    };
  }, []);

  const scrollMenu = () => {
    const element = scrollContainerRef.current;

    if (!element) return;

    element.scrollBy({
      top: -120,
      behavior: "smooth",
    });
  };

  const setTooltipFromElement = (title, element, backgroundColor) => {
    if (!title) return;

    if (tooltipHideTimeoutRef.current) {
      clearTimeout(tooltipHideTimeoutRef.current);
      tooltipHideTimeoutRef.current = null;
    }

    const rect = element.getBoundingClientRect();

    const isRTL = i18n.dir() === "rtl";

    setIsTooltipVisible(false);

    setHoverTooltip({
      title,
      top: rect.top,
      height: rect.height,
      backgroundColor,
      isRTL,
      left: rect.right,
      right: window.innerWidth - rect.left,
    });

    requestAnimationFrame(() => {
      setIsTooltipVisible(true);
    });
  };

  const onItemEnter = (item) => (event) => {
    setHoveredItemId(item.id);

    setTooltipFromElement(
      item.title,
      event.currentTarget,
      hoverBackgroundColor,
    );
  };

  const onItemLeave = () => {
    setHoveredItemId(null);

    setIsTooltipVisible(false);

    tooltipHideTimeoutRef.current = setTimeout(() => {
      setHoverTooltip(null);
      tooltipHideTimeoutRef.current = null;
    }, 180);
  };

  return (
    <div className="z-50 fixed md:top-20 bottom-[5%] start-0 w-11 flex flex-col items-center justify-between overflow-visible h-[70%] md:h-[80%]">
      <div
        className="cursor-pointer w-8 h-8 flex items-center justify-center rounded-md text-white hover:text-indigo-200 sticky top-0"
        onClick={scrollMenu}
      >
        <FaChevronUp size={16} />
      </div>

      <div
        ref={scrollContainerRef}
        className="custom-scrollbar overflow-y-auto overflow-x-visible flex-1 min-h-0 w-full flex flex-col items-center"
      >
        <ul className="list-none  m-0 p-0 py-2 flex flex-col gap-3 items-center text-white text-xs w-full">
          {menuItems.map((item) => {
            const Icon = item.icon;

            const isHovered = hoveredItemId === item.id;

            const isHighlighted = item.active || isHovered;

            const overlayBackgroundColor = isHovered
              ? hoverBackgroundColor
              : item.active
                ? activeBackgroundColor
                : hoverBackgroundColor;

            return (
              <li
                key={item.id}
                data-title={item.title}
                className="w-full relative overflow-hidden flex justify-center items-center"
                onMouseEnter={onItemEnter(item)}
                onMouseLeave={onItemLeave}
              >
                <span
                  className="absolute top-0 right-0 z-0 h-full transition-all duration-500 ease-out"
                  style={{
                    width: isHighlighted ? "100%" : "0%",
                    backgroundColor: overlayBackgroundColor,
                  }}
                />

                <NavLink
                  to={item.to}
                  className="relative z-20 w-full flex items-center justify-center py-3"
                >
                  <Icon
                    size={item.size}
                    className={`transition-colors duration-300 ${
                      isHighlighted ? "text-white" : "text-white"
                    }`}
                  />
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>

      {hoverTooltip?.title && (
        <div
          className={`fixed z-[999999999999] flex items-center text-xs whitespace-nowrap transition-opacity duration-200 ease-out ${
            isTooltipVisible ? "opacity-100" : "opacity-0"
          }`}
          style={{
            top: hoverTooltip.top,
            height: hoverTooltip.height,
            background:
              hoverTooltip.backgroundColor || hoverBackgroundColor,
            color: "#ffffff",
            padding: "0 10px",
            borderBottom: "1px solid rgba(255,255,255,0.35)",
            pointerEvents: "none",

            ...(hoverTooltip.isRTL
              ? {
                  right: hoverTooltip.right,
                  borderRadius: "5px 0px 0px 5px",
                }
              : {
                  left: hoverTooltip.left,
                  borderRadius: "0px 5px 5px 0px",
                }),
          }}
        >
          {hoverTooltip.title}
        </div>
      )}
    </div>
  );
};
export default MenuSide;